import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';

const KEY = ['admin', 'languages'];
const wordingsKey = (code: string) => ['admin', 'languages', code, 'wordings'];

/** Every language in the catalogue, on or off, with how many chatbots offer it and how far its built-in lines are (1 November 2026). */
export function useAdminLanguages() {
  return useQuery({ queryKey: KEY, queryFn: () => admin.listAdminLanguages() });
}

/**
 * Switch a language on or off, or change its names, words and codes. The service answers with the row but without
 * the count of chatbots offering it, so the list is read again rather than patched with a row that says none do.
 */
export function useUpdateLanguage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, change }: { code: string; change: admin.UpdateAdminLanguageRequest }) => admin.updateAdminLanguage(code, change),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY, exact: true })
  });
}

/** The chatbot's built-in lines in one language, beside the English. */
export function useLanguageWordings(code: string | undefined) {
  return useQuery({
    queryKey: wordingsKey(code ?? ''),
    queryFn: () => admin.listLanguageWordings(code!),
    enabled: Boolean(code)
  });
}

/**
 * Correct one line, or mark it as read by a native speaker. The line comes back as saved and replaces its row; the
 * language list is read again because its counts ("30 of 36", reviewed) move with it.
 */
export function useUpdateWording(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, change }: { key: string; change: admin.UpdateLanguageWordingRequest }) => admin.updateLanguageWording(code, key, change),
    onSuccess: (res) => {
      qc.setQueryData<{ data: admin.LanguageWording[] }>(wordingsKey(code), (prev) =>
        prev ? { ...prev, data: prev.data.map((w) => (w.key === res.data.key ? res.data : w)) } : prev);
      void qc.invalidateQueries({ queryKey: KEY, exact: true });
    }
  });
}

/** Translate the lines a language lacks now (`all`: remake every machine line too); the platform pays. */
export function useTranslateWordings(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (all: boolean) => admin.translateLanguageWordings(code, { all }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: wordingsKey(code) });
      void qc.invalidateQueries({ queryKey: KEY, exact: true });
    }
  });
}
